import { useEffect, useMemo, useRef } from 'react';
import useNewRecommendations from '../../product/hooks/useNewRecommendations';
import Slider from '@/components/slider/Slider';
import RecommendedCard from './RecommendedCard';
import { RECOMMENDATIONS_BREAKPOINTS } from '../constants';
import { getRecommendationExclusions } from '../helpers';
import { useCartContext } from '@/utils/hooks/cart/useCartContext';
import { useReportRecommendations } from '../hooks/useRecommendationsTracking';
import i18n from '@/settings/i18n';

const t = i18n.init('cart');

const FrequentlyBoughtTogether = ({
  lineId,
  psku,
}: {
  lineId: string;
  psku: string | undefined;
}) => {
  const { cartLines, getLineItemInCart } = useCartContext();

  const excludeProducts = useMemo(
    () => getRecommendationExclusions(cartLines),
    [cartLines],
  );

  const { products: recommendations, loading: recommendationsLoading } =
    useNewRecommendations({
      psku,
      excludeProducts,
    });

  const initiallyInCart = useRef<Set<string> | null>(null);

  const orderedRecommendations = useMemo(() => {
    if (recommendations.length === 0) {
      return recommendations;
    }

    if (initiallyInCart.current === null) {
      initiallyInCart.current = new Set(
        recommendations
          .filter((sku) => {
            const offerId = sku?.offers?.[0]?.id;

            return offerId != null && getLineItemInCart(offerId) != null;
          })
          .map((sku) => sku.id),
      );
    }

    const inCart = initiallyInCart.current;

    return [...recommendations].sort(
      (a, b) => Number(inCart.has(b.id)) - Number(inCart.has(a.id)),
    );
  }, [recommendations]);

  const reportRecommendations = useReportRecommendations();

  // Report once the fetch settles, so an empty array means "nothing to show"
  // rather than "still loading". Reporting is idempotent per line item.
  useEffect(() => {
    if (!psku || recommendationsLoading) {
      return;
    }

    reportRecommendations?.(lineId, recommendations);
  }, [lineId, psku, recommendationsLoading, recommendations]);

  if (!psku) {
    return null;
  }

  if (recommendationsLoading) {
    return (
      <div className="mt-4 border-[#E6E7EB] text-[13px] lg:mt-0 lg:border-t lg:bg-[#FBFEFD] lg:p-4">
        <h2 className="font-medium uppercase tracking-wide text-[#4A5565]">
          {t`Frequently bought together`}
        </h2>
        <Slider
          hideArrow
          breakpoints={RECOMMENDATIONS_BREAKPOINTS}
          className="mt-2 overflow-hidden"
        >
          {[1, 2, 3, 4].map((recommendation) => (
            <div
              key={recommendation}
              className="h-[229.5px] animate-pulse rounded-2xl bg-[#F2F2F2]"
            />
          ))}
        </Slider>
      </div>
    );
  }

  if (recommendations.length > 0) {
    return (
      <div className="mt-4 rounded-b-2xl border-[#E6E7EB] text-[13px] lg:mt-0 lg:border-t lg:bg-[#FBFEFD] lg:p-4">
        <h2 className="font-medium uppercase tracking-wide text-[#4A5565]">
          {t`Frequently bought together`}
        </h2>
        <div className="relative">
          <Slider
            hideArrow
            breakpoints={RECOMMENDATIONS_BREAKPOINTS}
            className="mt-2 overflow-hidden"
          >
            {orderedRecommendations.map((recommendation, index) => {
              const sku = recommendation;
              const offer = sku?.offers?.[0];
              const product = sku?.product;

              if (!sku || !offer || !product) {
                return null;
              }

              return (
                <RecommendedCard
                  key={recommendation.id}
                  sku={sku}
                  offer={offer}
                  product={product}
                  slotNumber={index + 1}
                  parentLineId={lineId}
                />
              );
            })}
          </Slider>
        </div>
      </div>
    );
  }

  return null;
};

export default FrequentlyBoughtTogether;
